/**
 * ConnectContainer: the OAuth consent page an assistant (ChatGPT) sends a
 * person to when it asks to connect their Splyt account.
 * Route: /connect/:requestId
 *
 * Steps: load the request, then phone and code (unless a real session already
 * exists), then consent. The request id stays in the URL so a refresh reloads
 * the request; the pending SMS confirmation is module state, so a refresh on
 * the code step lands on the phone step again.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useJoinPageChrome from '../join/useJoinPageChrome';
import { ConnectState } from './ConnectShell';
import ConnectPhoneStep from './ConnectPhoneStep';
import ConnectCodeStep from './ConnectCodeStep';
import ConnectConsentStep from './ConnectConsentStep';
import { fetchConnectRequest, waitForAuthUser, profileNeedsName } from './connectClient';
import { STEP, firstStepForUser, isRequestExpired, assistantName } from './connectHelpers';

const PHASE = Object.freeze({
  LOADING: 'loading',
  EXPIRED: 'expired',
  FAILED: 'failed',
  STEPS: 'steps',
});

export default function ConnectContainer() {
  useJoinPageChrome();
  const { requestId } = useParams();

  const [phase, setPhase] = useState(PHASE.LOADING);
  const [request, setRequest] = useState(null);
  const [step, setStep] = useState(STEP.PHONE);
  const [user, setUser] = useState(null);
  const [needsName, setNeedsName] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  // A spinner while the profile name is read, then the consent step.
  const enterConsent = useCallback(async (signedInUser) => {
    setPhase(PHASE.LOADING);
    const nameless = await profileNeedsName(signedInUser && signedInUser.uid);
    if (!mountedRef.current) return;
    setUser(signedInUser);
    setNeedsName(nameless);
    setStep(STEP.CONSENT);
    setPhase(PHASE.STEPS);
  }, []);

  useEffect(() => {
    let active = true;
    setPhase(PHASE.LOADING);

    (async () => {
      const [result, authUser] = await Promise.all([
        fetchConnectRequest(requestId)
          .then((value) => ({ value }))
          .catch((error) => ({ error })),
        waitForAuthUser(),
      ]);
      if (!active) return;

      if (result.error) {
        setPhase(result.error.gone ? PHASE.EXPIRED : PHASE.FAILED);
        return;
      }

      setRequest(result.value);
      if (isRequestExpired(result.value)) {
        setPhase(PHASE.EXPIRED);
        return;
      }

      if (firstStepForUser(authUser) === STEP.CONSENT) {
        await enterConsent(authUser);
      } else {
        setStep(STEP.PHONE);
        setPhase(PHASE.STEPS);
      }
    })();

    return () => {
      active = false;
    };
  }, [requestId, enterConsent]);

  const goToPhone = useCallback(() => setStep(STEP.PHONE), []);
  const goToCode = useCallback(() => setStep(STEP.CODE), []);
  const markGone = useCallback(() => setPhase(PHASE.EXPIRED), []);

  const clientName = request ? request.clientName : '';

  if (phase === PHASE.LOADING) {
    return <ConnectState />;
  }

  if (phase === PHASE.EXPIRED) {
    return (
      <ConnectState
        title="This connection request has expired."
        text={`Go back to ${assistantName(clientName)} and try again.`}
      />
    );
  }

  if (phase === PHASE.FAILED) {
    return (
      <ConnectState
        title="Couldn't load this request."
        text="Check your connection, then refresh this page."
      />
    );
  }

  if (step === STEP.CODE) {
    return <ConnectCodeStep onVerified={enterConsent} onWrongNumber={goToPhone} />;
  }

  if (step === STEP.CONSENT && request) {
    return (
      <ConnectConsentStep
        request={request}
        user={user}
        needsName={needsName}
        onGone={markGone}
      />
    );
  }

  return <ConnectPhoneStep clientName={clientName} onCodeSent={goToCode} />;
}
